/**
 * ProgressIndicator.js
 * Talkroute
 *
 * Created by Vel Genov on 10/15/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */

import React from "react";
import { connect } from "react-redux";
import dataStateEnum from "../../constants/dataStateConstants";

/**
 * Progress bar states
 */
const progressValues = {
  STARTING: 0,
  SENDING: 80,
  FINISHED: 100,
};

interface IProgressIndicatorProps {
  sendingState: string;
  selectedConversationId: string;
}

/**
 * Displays a message sending progress bar
 */
class ProgressIndicator extends React.Component<IProgressIndicatorProps, any> {
  public progressBar;

  static defaultProps = {
    sendingState: dataStateEnum.READY,
    selectedConversationId: "",
  };

  /**
   * Animate the progress bar from the current value to the new value
   *
   * @param newValue the value to progress to
   */
  animateProgress = (newValue) => {
    const { progressBar } = this;
    if (progressBar) {
      const currentValue = progressBar.value;
      const step = (newValue * 16) / 500;

      this.animate(currentValue, newValue, step);
    }
  };

  /**
   * Animate the progress bar from the current value to the new value with a given step
   *
   * @param currentValue the current value
   * @param newValue the new value
   * @param step the step to animate with
   */
  animate = (currentValue, newValue, step) => {
    const nextValue = step + currentValue;
    this.setProgressValue(nextValue);
    if (nextValue < newValue) {
      requestAnimationFrame(() => {
        this.animate(nextValue, newValue, step);
      });
    } else {
      this.animationFinished();
    }
  };

  /**
   * Called to clean up when the animation finishes
   */
  animationFinished = () => {
    const { sendingState } = this.props;

    if (sendingState === dataStateEnum.READY) {
      this.isHidden(true);
    }
  };

  /**
   * Animate the progress bar when the user starts sending a message
   */
  progressAnimateStartSending = () => {
    this.setProgressValue(progressValues.STARTING);
    this.isHidden(false);
    this.animateProgress(progressValues.SENDING);
  };

  /**
   * Start completing the animation
   */
  progressFinishAnimation = () => {
    this.animateProgress(progressValues.FINISHED);
  };

  /**
   * Set the progress bar value without animating
   *
   * @param newValue the new progress bar value
   */
  setProgressValue = (newValue) => {
    const { progressBar } = this;

    if (progressBar) {
      progressBar.value = newValue;
    }
  };

  /**
   * Hides/shows the progress bar
   *
   * @param isHidden a boolean representing the new hidden state of the progress bar
   */
  isHidden = (isHidden) => {
    const { progressBar } = this;

    if (progressBar) {
      progressBar.style.display = isHidden ? "none" : "inline";
    }
  };

  componentDidMount() {
    this.setProgressValue(progressValues.STARTING);
    this.isHidden(true);
  }

  componentDidUpdate(prevProps) {
    const { sendingState, selectedConversationId } = this.props;
    const {
      sendingState: prevSendingState,
      selectedConversationId: prevSelectedConversationId,
    } = prevProps;

    if (selectedConversationId !== prevSelectedConversationId) {
      this.progressFinishAnimation();
      this.isHidden(true);
    }

    switch (sendingState) {
      case dataStateEnum.READY:
        if (prevSendingState === dataStateEnum.LOADING) {
          // Finished sending
          this.progressFinishAnimation();
        }
        break;
      case dataStateEnum.LOADING:
        if (prevSendingState === dataStateEnum.READY) {
          // Started sending
          this.progressAnimateStartSending();
        }
        break;
      default:
        this.setProgressValue(progressValues.STARTING);
    }
  }

  componentWillUnmount() {
    this.isHidden(true);
  }

  render() {
    return (
      <div
        style={{
          width: "100%",
          height: "4px",
          position: "absolute",
          zIndex: 1,
        }}
      >
        <progress
          ref={(progress) => {
            this.progressBar = progress;
          }}
          max="100"
          style={{
            display: "inline",
          }}
        />
      </div>
    );
  }
}

/**
 * Map props to a redux state
 * @param {*} state
 */
const mapStateToProps = (state) => ({
  sendingState: state.textMessageDraft.sendingState,
  selectedConversationId: state.conversations.selectedConversationId,
});

export default connect(mapStateToProps, {})(ProgressIndicator);
